import { useEffect, useRef, useState, ReactNode, ElementType } from "react";

interface RevealProps {
  children: ReactNode;
  className?: string;
  /** Ritardo in ms per effetto a cascata */
  delay?: number;
  /** Elemento da renderizzare (default: div) */
  as?: ElementType;
}

/**
 * Rivela i figli con una comparsa dolce quando entrano nel viewport.
 * Robusto: oltre all'IntersectionObserver usa un controllo su scroll/resize
 * (getBoundingClientRect), così nessuna sezione può restare nascosta anche
 * con scroll molto veloci. Rispetta prefers-reduced-motion.
 */
const Reveal = ({ children, className = "", delay = 0, as: Tag = "div" }: RevealProps) => {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setVisible(true);
      return;
    }

    let done = false;
    const reveal = () => {
      if (done) return;
      done = true;
      setVisible(true);
      cleanup();
    };

    const check = () => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight - 40) reveal();
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) reveal();
      },
      { rootMargin: "0px 0px -40px 0px" },
    );
    io.observe(el);
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);

    function cleanup() {
      io.disconnect();
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    }

    // controllo iniziale (elemento già in vista al caricamento)
    check();
    return cleanup;
  }, []);

  return (
    <Tag
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      } ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
